<template>
  <div
    :class="['player-names', { 'player-names--right': reverse }]"
    @transitionend="handleTransitionEnd"
  >
    <span class="player-name-1 text-body-1 text-truncate" style="line-height: 1.6;">{{ player1Name }}</span>
    <span v-if="player2Name" class="player-name-ampersand text-body-1" style="line-height: 1.6;">&nbsp;&amp;&nbsp;</span>
    <span v-if="player2Name" class="player-name-2 text-body-1 text-truncate" style="line-height: 1.6;">{{ player2Name }}</span>
  </div>
</template>

<script>
export default {
  name: "AnimatedPlayerNames",
  props: {
    player1Name: {
      type: String,
      required: true
    },
    player2Name: {
      type: String,
      default: ""
    },
    reverse: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      closing: false
    };
  },
  methods: {
    measure () {
      this.closing = false;
      if (this.reverse) {
        this.measureRight();
      } else {
        this.measureLeft();
      }
    },
    close () {
      this.closing = true;
      if (this.reverse) {
        this.closeRight();
      } else {
        this.closeLeft();
      }
    },
    measureLeft () {
      const container = this.$el;
      if (!container) return;

      const p1 = container.querySelector(".player-name-1");
      const amp = container.querySelector(".player-name-ampersand");
      const p2 = container.querySelector(".player-name-2");
      if (!p1) return;

      const isSingle = !this.player2Name;
      if (!isSingle && (!amp || !p2)) return;

      const p1Rect = p1.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const p1Width = p1Rect.width;
      const p1OffsetTop = p1Rect.top - containerRect.top;
      const ampWidth = isSingle ? 0 : amp.getBoundingClientRect().width;
      const p2Width = isSingle ? 0 : p2.getBoundingClientRect().width;
      const totalWidth = p1Width + ampWidth + p2Width;

      const versusRow = container.parentElement.parentElement;
      const versusRowRect = versusRow.getBoundingClientRect();

      const groupLeftInRow = (versusRowRect.width - totalWidth) / 2;
      const groupLeft = groupLeftInRow - (containerRect.left - versusRowRect.left);

      const centeredLeft = isSingle ? 0 : (p1Width - p2Width) / 2;
      const startTop = isSingle ? 0 : (p1Rect.height + 1);

      this._p1StartLeftLeft = p1Rect.left - containerRect.left;
      this._p1StartTopLeft = p1OffsetTop;

      if (!isSingle) {
        this._p2StartLeft = centeredLeft;
        this._p2StartTop = startTop + p1OffsetTop;
        this._p1WidthLeft = p1Width;
        this._ampWidthLeft = ampWidth;
      }

      const containerHeight = container.offsetHeight;
      container.style.height = containerHeight + "px";

      const dummy = document.createElement("div");
      dummy.className = "player-name-dummy";
      dummy.style.width = p1Rect.width + "px";
      dummy.style.height = containerHeight + "px";
      dummy.style.flexShrink = "0";
      container.prepend(dummy);

      p1.style.position = "absolute";
      p1.style.left = this._p1StartLeftLeft + "px";
      p1.style.top = this._p1StartTopLeft + "px";

      if (!isSingle) {
        amp.style.position = "absolute";
        amp.style.left = (this._p1StartLeftLeft + p1Width) + "px";
        amp.style.top = p1OffsetTop + "px";

        p2.style.position = "absolute";
        p2.style.left = centeredLeft + "px";
        p2.style.top = (startTop + p1OffsetTop) + "px";
      }

      void (isSingle ? dummy : amp).offsetHeight;

      if (!isSingle) {
        amp.style.opacity = "1";
        amp.style.left = (groupLeft + p1Width) + "px";
        p2.style.left = (groupLeft + p1Width + ampWidth) + "px";
        p2.style.top = p1OffsetTop + "px";
      }

      p1.style.left = groupLeft + "px";
      p1.style.top = p1OffsetTop + "px";
    },
    closeLeft () {
      const container = this.$el;
      if (!container) return;

      const isSingle = !this.player2Name;
      const p1 = container.querySelector(".player-name-1");
      if (!p1) return;

      p1.style.left = this._p1StartLeftLeft + "px";
      p1.style.top = this._p1StartTopLeft + "px";

      if (!isSingle) {
        const amp = container.querySelector(".player-name-ampersand");
        const p2 = container.querySelector(".player-name-2");
        if (!amp || !p2) return;

        amp.style.opacity = "0";
        amp.style.left = (this._p1StartLeftLeft + this._ampWidthLeft) + "px";
        p2.style.left = this._p2StartLeft + "px";
        p2.style.top = this._p2StartTop + "px";
      }
    },
    cleanupLeft () {
      const container = this.$el;
      if (!container) return;

      const dummy = container.querySelector(".player-name-dummy");
      if (dummy) dummy.remove();

      const p1 = container.querySelector(".player-name-1");
      if (p1) {
        p1.style.position = "";
        p1.style.left = "";
        p1.style.top = "";
      }

      if (this.player2Name) {
        const amp = container.querySelector(".player-name-ampersand");
        const p2 = container.querySelector(".player-name-2");
        if (amp) {
          amp.style.position = "";
          amp.style.left = "";
          amp.style.top = "";
          amp.style.opacity = "";
        }
        if (p2) {
          p2.style.position = "";
          p2.style.left = "";
          p2.style.top = "";
        }
      }

      container.style.height = "";

      this.closing = false;
    },
    measureRight () {
      const container = this.$el;
      if (!container) return;

      const p1 = container.querySelector(".player-name-1");
      const amp = container.querySelector(".player-name-ampersand");
      const p2 = container.querySelector(".player-name-2");
      if (!p1) return;

      const isSingle = !this.player2Name;
      if (!isSingle && (!amp || !p2)) return;

      const p1Rect = p1.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const p1Width = p1Rect.width;
      const ampWidth = isSingle ? 0 : amp.getBoundingClientRect().width;
      const p2Width = isSingle ? 0 : p2.getBoundingClientRect().width;
      const totalWidth = p1Width + ampWidth + p2Width;

      const versusRow = container.parentElement.parentElement;
      const versusRowRect = versusRow.getBoundingClientRect();

      const groupLeftInRow = (versusRowRect.width - totalWidth) / 2;
      const groupLeft = groupLeftInRow - (containerRect.left - versusRowRect.left);

      const centeredLeft = isSingle ? p1Rect.left - containerRect.left : (p2Width - p1Width) / 2;

      this._p1StartLeftRight = centeredLeft;
      this._p1StartTopRight = 0;
      this._p1WidthRight = p1Width;
      this._ampWidthRight = ampWidth;

      let p2OffsetTop = 0;
      if (!isSingle) {
        const p2Rect = p2.getBoundingClientRect();
        this._p2StartLeftRight = p2Rect.left - containerRect.left;
        this._p2StartTopRight = p2Rect.top - containerRect.top;
      }

      const containerHeight = container.offsetHeight;
      container.style.height = containerHeight + "px";

      p1.style.position = "absolute";
      p1.style.left = centeredLeft + "px";
      p1.style.top = "0px";

      const dummy = document.createElement("div");
      dummy.className = "player-name-dummy";
      dummy.style.width = p1Rect.width + "px";
      dummy.style.height = (isSingle ? containerHeight : (p1Rect.height + 1)) + "px";
      dummy.style.flexShrink = "0";
      container.prepend(dummy);

      if (!isSingle) {
        amp.style.position = "absolute";
        amp.style.left = (this._p2StartLeftRight - ampWidth) + "px";
        amp.style.top = this._p2StartTopRight + "px";

        p2.style.position = "absolute";
        p2.style.left = this._p2StartLeftRight + "px";
        p2.style.top = this._p2StartTopRight + "px";
      }

      void (isSingle ? dummy : amp).offsetHeight;

      if (!isSingle) {
        p2OffsetTop = p2.offsetTop;
        amp.style.top = p2OffsetTop + "px";
        amp.style.opacity = "1";
        amp.style.left = (groupLeft + p1Width) + "px";
        p2.style.left = (groupLeft + p1Width + ampWidth) + "px";
        p2.style.top = p2OffsetTop + "px";
      }

      p1.style.left = groupLeft + "px";
      p1.style.top = (isSingle ? (p1Rect.top - containerRect.top) : p2OffsetTop) + "px";
    },
    closeRight () {
      const container = this.$el;
      if (!container) return;

      const isSingle = !this.player2Name;
      const p1 = container.querySelector(".player-name-1");
      if (!p1) return;

      p1.style.left = this._p1StartLeftRight + "px";
      p1.style.top = this._p1StartTopRight + "px";

      if (!isSingle) {
        const amp = container.querySelector(".player-name-ampersand");
        const p2 = container.querySelector(".player-name-2");
        if (!amp || !p2) return;

        amp.style.opacity = "0";
        amp.style.left = (this._p2StartLeftRight - this._ampWidthRight) + "px";
        amp.style.top = this._p2StartTopRight + "px";

        p2.style.left = this._p2StartLeftRight + "px";
        p2.style.top = this._p2StartTopRight + "px";
      }
    },
    cleanupRight () {
      const container = this.$el;
      if (!container) return;

      const dummy = container.querySelector(".player-name-dummy");
      if (dummy) dummy.remove();

      const p1 = container.querySelector(".player-name-1");
      if (p1) {
        p1.style.position = "";
        p1.style.left = "";
        p1.style.top = "";
      }

      if (this.player2Name) {
        const amp = container.querySelector(".player-name-ampersand");
        const p2 = container.querySelector(".player-name-2");
        if (amp) {
          amp.style.position = "";
          amp.style.left = "";
          amp.style.top = "";
          amp.style.opacity = "";
        }
        if (p2) {
          p2.style.position = "";
          p2.style.left = "";
          p2.style.top = "";
        }
      }

      container.style.height = "";

      this.closing = false;
    },
    handleTransitionEnd (event) {
      if (this.reverse) {
        if (this.closing && (event.propertyName === "opacity" || event.propertyName === "left")) {
          this.cleanupRight();
        }
      } else {
        if (this.closing && (event.propertyName === "opacity" || event.propertyName === "left")) {
          this.cleanupLeft();
        }
      }
    }
  }
};
</script>

<style scoped>
.player-names {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}
.player-name-1 {
  transition: all .3s ease;
  white-space: nowrap;
}
.player-name-ampersand {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  transition: opacity .3s ease, left .3s ease;
  white-space: nowrap;
  pointer-events: none;
}
.player-name-2 {
  transition: all .3s ease;
  white-space: nowrap;
}
.player-names--right .player-name-2 {
  white-space: nowrap;
}
.player-names--right .player-name-ampersand {
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  transition: opacity .3s ease, left .3s ease;
  white-space: nowrap;
  pointer-events: none;
}
.player-names--right .player-name-1 {
  transition: all .3s ease;
  white-space: nowrap;
}
</style>
